import * as THREE from 'three/webgpu'
import { ImportedModelSelectionObjectResolver } from '../selection/ImportedModelSelectionObjectResolver'
import type { SelectionInteractionMeta } from '../render/selection-extension'
import { SW_HOVER_COLOR } from '../render/sw-theme'
import { addNonSelectableObject3DToParent } from '../../utils/scene-object/addObject3DToParent'
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature'

/**
 * imported-model 设计树 hover 包围盒上下文。
 *
 * 设计说明：
 * 1. 该上下文只消费 InteractionStateManager 已经归一的 hover 状态；
 * 2. 这里不依赖具体视觉状态类，保证默认零件场景与装配体场景共用同一套 imported-model 树 hover 语义；
 * 3. 只有 `source === 'feature-tree'` 的程序化 hover 会进入包围盒逻辑，场景鼠标 hover 仍走原有点/线/面命中链路。
 */
export interface ImportedModelTreeHoverBoundingBoxContext {
  /** 当前单对象 hover。 */
  hoveredObject: THREE.Object3D | null
  /** 当前单对象 hover 的交互元信息。 */
  hoveredInteractionMeta?: SelectionInteractionMeta
  /** 当前程序化多目标 hover 对象列表。 */
  programmaticHoveredObjects: THREE.Object3D[]
  /** 当前程序化多目标 hover 的交互元信息。 */
  programmaticHoveredInteractionMeta?: SelectionInteractionMeta
}

/**
 * imported-model 树 hover 包围盒渲染器运行时依赖。
 */
export interface ImportedModelTreeHoverBoundingBoxRendererOptions {
  /** 主场景，用于 imported-model 选择对象解析。 */
  scene: THREE.Scene
  /** 辅助场景，包围盒 helper 挂载在这里而不是污染主模型层。 */
  sceneHelpers: THREE.Object3D
  /** 按运行时对象 ID 查询场景对象。 */
  findSceneObjectById(objectId: string): THREE.Object3D | null
}

interface ImportedModelBoundingBoxDescriptor {
  /** 当前包围盒目标键。 */
  key: string
  /** 当前包围盒。 */
  box: THREE.Box3
}

/**
 * imported-model 设计树 hover 包围盒渲染器。
 *
 * 设计说明：
 * 1. 单对象 imported-model 树 hover 由 `ImportedModelHighlightController` 统一接管；
 * 2. 本类只保留给多目标程序化 hover，例如线性零部件阵列树节点一次对应多个运行时对象；
 * 3. 这里不接管场景鼠标 hover 或 selected overlay，避免 imported 实体视觉出现两套实现。
 */
export class ImportedModelTreeHoverBoundingBoxRenderer {
  /** 运行时依赖。 */
  private readonly options: ImportedModelTreeHoverBoundingBoxRendererOptions
  /** imported-model 选择对象归一化解析器。 */
  private readonly importedModelSelectionObjectResolver: ImportedModelSelectionObjectResolver
  /** 当前移入包围盒 helper 映射。 */
  private readonly hoverBoundingBoxHelpers: Map<string, THREE.Box3Helper> = new Map()

  /**
   * 创建设计树 hover 包围盒渲染器。
   *
   * @param options 运行时依赖。
   */
  public constructor(options: ImportedModelTreeHoverBoundingBoxRendererOptions) {
    this.options = options
    this.importedModelSelectionObjectResolver = new ImportedModelSelectionObjectResolver({
      scene: options.scene,
      getSceneObjectById: (objectId: string) => options.findSceneObjectById(objectId),
    })
  }

  /**
   * 按当前交互上下文同步包围盒 helper。
   *
   * @param context 当前视觉状态上下文。
   */
  public apply(context: ImportedModelTreeHoverBoundingBoxContext): void {
    const boundingBoxDescriptors = this.resolveImportedModelHoverBoundingBoxDescriptors(context)
    if (boundingBoxDescriptors.length === 0) {
      this.clear()
      return
    }

    const activeKeys = new Set(boundingBoxDescriptors.map((descriptor) => descriptor.key))
    Array.from(this.hoverBoundingBoxHelpers.keys()).forEach((key) => {
      if (!activeKeys.has(key)) {
        this.disposeHoverBoundingBoxHelper(key)
      }
    })

    boundingBoxDescriptors.forEach((descriptor) => {
      const currentHelper = this.hoverBoundingBoxHelpers.get(descriptor.key)
      if (!currentHelper) {
        this.hoverBoundingBoxHelpers.set(
          descriptor.key,
          this.createHoverBoundingBoxHelper(descriptor.box),
        )
        return
      }

      currentHelper.box.copy(descriptor.box)
      currentHelper.visible = true
      currentHelper.updateMatrixWorld(true)
    })
  }

  /**
   * 清空当前所有包围盒 helper。
   */
  public clear(): void {
    Array.from(this.hoverBoundingBoxHelpers.keys()).forEach((key) => {
      this.disposeHoverBoundingBoxHelper(key)
    })
  }

  /**
   * 释放 renderer 持有的 helper 资源。
   */
  public dispose(): void {
    this.clear()
  }

  /**
   * 解析当前 imported-model 移入应显示的全部包围盒描述符。
   *
   * @param context 当前视觉状态上下文。
   * @returns 包围盒描述符列表。
   */
  private resolveImportedModelHoverBoundingBoxDescriptors(
    context: ImportedModelTreeHoverBoundingBoxContext,
  ): ImportedModelBoundingBoxDescriptor[] {
    const descriptors: ImportedModelBoundingBoxDescriptor[] = []
    const singleDescriptor = this.resolveImportedModelHoverBoundingBoxDescriptor(context)
    if (singleDescriptor) {
      descriptors.push(singleDescriptor)
    }

    if (context.programmaticHoveredInteractionMeta?.source !== 'feature-tree') {
      return descriptors
    }

    context.programmaticHoveredObjects.forEach((hoveredObject) => {
      const descriptor = this.resolveImportedModelBoundingBoxDescriptor(
        hoveredObject,
        context.programmaticHoveredInteractionMeta,
      )
      if (descriptor) {
        descriptors.push(descriptor)
      }
    })

    return descriptors
  }

  /**
   * 解析当前单对象 imported-model 移入包围盒。
   *
   * @param context 当前视觉状态上下文。
   * @returns 包围盒描述符；无需显示时返回 `null`。
   */
  private resolveImportedModelHoverBoundingBoxDescriptor(
    context: ImportedModelTreeHoverBoundingBoxContext,
  ): ImportedModelBoundingBoxDescriptor | null {
    if (context.hoveredInteractionMeta?.source !== 'feature-tree') {
      return null
    }
    if (this.isSingleImportedModelEntityHoverHandledByHighlighter(context.hoveredInteractionMeta)) {
      return null
    }

    const hoveredObject = context.hoveredObject
    if (!hoveredObject) {
      return null
    }

    return this.resolveImportedModelBoundingBoxDescriptor(hoveredObject, context.hoveredInteractionMeta)
  }

  /**
   * 判断单对象 imported-model 设计树 hover 是否已经由 highlighter 统一接管。
   *
   * @param interactionMeta 当前 hover 交互元信息。
   * @returns 是否应跳过当前 renderer。
   */
  private isSingleImportedModelEntityHoverHandledByHighlighter(
    interactionMeta: SelectionInteractionMeta | undefined,
  ): boolean {
    if (interactionMeta?.source !== 'feature-tree' || interactionMeta.semantic !== 'entity') {
      return false
    }
    const role = interactionMeta.glbNodeRole
    return role === 'assembly'
      || role === 'part'
      || role === 'component'
      || role === 'group'
      || role === 'body-folder'
      || role === 'body'
  }

  /**
   * 按指定对象和交互语义解析 imported-model 包围盒。
   *
   * @param hoveredObject 当前移入对象。
   * @param interactionMeta 当前移入语义。
   * @returns 包围盒描述符；无需显示时返回 `null`。
   */
  private resolveImportedModelBoundingBoxDescriptor(
    hoveredObject: THREE.Object3D,
    interactionMeta: SelectionInteractionMeta | undefined,
  ): ImportedModelBoundingBoxDescriptor | null {
    const explicitDescriptor = this.resolveExplicitFeatureTreeHoverBoundingBoxDescriptor(interactionMeta)
    if (explicitDescriptor) {
      return explicitDescriptor
    }

    if (!this.importedModelSelectionObjectResolver.isImportedModelObject(hoveredObject)) {
      return null
    }

    const explicitObjectIds = Array.isArray(interactionMeta?.entityHighlightTargets)
      ? interactionMeta.entityHighlightTargets.map((target) => target.objectId)
      : []
    const targetObject = interactionMeta?.glbNodeRole === 'body'
      ? this.importedModelSelectionObjectResolver.resolveSelectionObject(hoveredObject) ?? hoveredObject
      : this.importedModelSelectionObjectResolver.resolveTransformRootObject(hoveredObject)
        ?? this.importedModelSelectionObjectResolver.resolveSelectionObject(hoveredObject)
        ?? hoveredObject
    const targetMeshes = this.collectImportedVisibleMeshes(targetObject, { explicitObjectIds })
    if (targetMeshes.length <= 0) {
      const box = new THREE.Box3().setFromObject(targetObject)
      return box.isEmpty()
        ? null
        : {
            key: targetObject.uuid,
            box,
          }
    }

    const box = new THREE.Box3()
    targetMeshes.forEach((targetMesh) => {
      box.expandByObject(targetMesh)
    })
    if (box.isEmpty()) {
      return null
    }

    return {
      key: explicitObjectIds.length > 0
        ? explicitObjectIds.join('|')
        : targetMeshes.map((targetMesh) => targetMesh.uuid).join('|'),
      box,
    }
  }

  /**
   * 根据设计树交互元数据中的显式目标解析包围盒。
   *
   * 设计说明：
   * 1. `.puppy` 导入后的实体节点是虚拟 Body 节点，树节点通过
   *    `entityHighlightTargets` 指向真实运行时 mesh；
   * 2. 显式目标是 Body 级 hover 的最高优先级，不能再要求被引用对象必须是当前 hover 对象的子节点；
   * 3. 该入口只接受 `source === 'feature-tree'`，不会改变场景鼠标 hover 的点/线/面高亮行为。
   *
   * @param interactionMeta 当前 hover 交互元信息。
   * @returns 显式目标对应的包围盒描述；缺少有效目标时返回 `null`。
   */
  private resolveExplicitFeatureTreeHoverBoundingBoxDescriptor(
    interactionMeta: SelectionInteractionMeta | undefined,
  ): ImportedModelBoundingBoxDescriptor | null {
    if (interactionMeta?.source !== 'feature-tree') {
      return null
    }

    const explicitObjectIds = Array.isArray(interactionMeta.entityHighlightTargets)
      ? Array.from(new Set(interactionMeta.entityHighlightTargets
          .map((target) => target.objectId?.trim() ?? '')
          .filter((objectId) => objectId.length > 0)))
      : []
    if (explicitObjectIds.length <= 0) {
      return null
    }

    const targetObjects = explicitObjectIds
      .map((objectId) => this.options.findSceneObjectById(objectId))
      .filter((object): object is THREE.Object3D => Boolean(object))
    if (targetObjects.length <= 0) {
      return null
    }

    const box = new THREE.Box3()
    targetObjects.forEach((targetObject) => {
      box.expandByObject(targetObject)
    })
    if (box.isEmpty()) {
      return null
    }

    return {
      key: explicitObjectIds.join('|'),
      box,
    }
  }

  /**
   * 创建 imported-model 移入包围盒 helper。
   *
   * @param box 当前世界包围盒。
   * @returns 包围盒 helper。
   */
  private createHoverBoundingBoxHelper(box: THREE.Box3): THREE.Box3Helper {
    const helper = new THREE.Box3Helper(box.clone(), SW_HOVER_COLOR)
    const helperMaterial = helper.material
    if (Array.isArray(helperMaterial)) {
      helperMaterial.forEach((material) => {
        material.depthTest = false
        material.depthWrite = false
        material.transparent = true
        material.opacity = 1
      })
    } else {
      helperMaterial.depthTest = false
      helperMaterial.depthWrite = false
      helperMaterial.transparent = true
      helperMaterial.opacity = 1
    }
    helper.name = 'imported-model-tree-hover-bounding-box'
    helper.renderOrder = 8
    helper.userData.skipSelection = true
    helper.userData.isAssemblyHoverBoundingBox = true
    helper.userData.isImportedModelTreeHoverBoundingBox = true

    addNonSelectableObject3DToParent(helper, this.options.sceneHelpers)
    return helper
  }

  /**
   * 释放指定包围盒 helper。
   *
   * @param key helper 键。
   */
  private disposeHoverBoundingBoxHelper(key: string): void {
    const helper = this.hoverBoundingBoxHelpers.get(key)
    if (!helper) {
      return
    }

    if (helper.parent) {
      helper.parent.remove(helper)
    }
    helper.geometry.dispose()
    const helperMaterial = helper.material
    if (Array.isArray(helperMaterial)) {
      helperMaterial.forEach((material) => material.dispose())
    } else {
      helperMaterial.dispose()
    }
    this.hoverBoundingBoxHelpers.delete(key)
  }

  /**
   * 收集当前 imported-model 语义对象下可用于 bbox 的 visible mesh。
   *
   * @param rootObject 语义根对象。
   * @param options 收集选项。
   * @returns visible mesh 列表。
   */
  private collectImportedVisibleMeshes(
    rootObject: THREE.Object3D,
    options: {
      explicitObjectIds?: readonly string[]
    } = {},
  ): THREE.Mesh[] {
    const explicitObjectIds = Array.isArray(options.explicitObjectIds)
      ? options.explicitObjectIds.filter((objectId) => typeof objectId === 'string' && objectId.length > 0)
      : []
    const candidateRoots: THREE.Object3D[] = []

    if (explicitObjectIds.length > 0) {
      if (explicitObjectIds.includes(rootObject.uuid)) {
        candidateRoots.push(rootObject)
      }
      explicitObjectIds.forEach((objectId) => {
        if (objectId === rootObject.uuid) {
          return
        }
        const matchedObject = rootObject.getObjectByProperty('uuid', objectId) ?? null
        if (matchedObject) {
          candidateRoots.push(matchedObject)
        }
      })
    } else {
      candidateRoots.push(rootObject)
    }

    const visibleMeshMap = new Map<string, THREE.Mesh>()
    candidateRoots.forEach((candidateRoot) => {
      candidateRoot.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) {
          return
        }
        if (!object.visible) {
          return
        }
        if (object.userData?.skipSelection || object.userData?.isImportedModelOverlay) {
          return
        }
        if (this.isImportedModelTopologyProxyObject(object)) {
          return
        }

        if (object.name.startsWith('imported-topology-host:')) {
          return
        }
        visibleMeshMap.set(object.uuid, object)
      })
    })

    return Array.from(visibleMeshMap.values())
  }

  /**
   * 判断对象是否为 imported-model 子树里的运行时拓扑代理。
   *
   * @param object 待判断对象。
   * @returns 命中 imported-model Face / Line / Point 代理时返回 `true`。
   */
  private isImportedModelTopologyProxyObject(object: THREE.Object3D): boolean {
    const isTopologyProxyObject = isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)
    if (!isTopologyProxyObject) {
      return false
    }

    return this.importedModelSelectionObjectResolver.isImportedModelObject(object)
  }
}
